/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();

import * as Accordion from '@radix-ui/react-accordion'
import React, { useMemo } from 'react'

import { KitAccordionCategoryItem } from '@/components/e-commerce/kits/kit-accordion/KitAccordionCategoryItem'
import { MtaKitStoryblok } from '@/lib/storyblok/types/generated-types'

/** A type to group kits by their category. */
type GroupedKits = {
  /** The category of the kit. */
  [key in MtaKitStoryblok['category']]?: Array<MtaKitStoryblokWithSlug>
}

/** A type that extends MtaKitStoryblok with a slug property. */
type MtaKitStoryblokWithSlug = MtaKitStoryblok & {
  /** The slug of the kit, used for routing. */
  slug: string
}

/** A description of the available properties for this component. */
type KitAccordionOverviewProps = {
  /** The kits to display in the accordion. */
  kits: Array<MtaKitStoryblokWithSlug>

  /** The categories to display in the accordion. */
  categories: Array<{
    /** The text to display in the title of the accordion item. */
    header: string

    /** The colour of the header. */
    headerColour: string

    /** A short description of the category. */
    description: React.ReactNode

    /** The category of kits to show in the accordion item. */
    category: 'comprehensive' | 'education-young-people' | 'specialist-kits' | 'bespoke-kits'
  }>
}

/**
 * A component that displays an accordion overview of kits, grouped by category.
 */
const KitAccordionOverview: React.FC<KitAccordionOverviewProps> = ({ kits, categories }) => {
  /** Group the kits by their category. */
  const groupedKits = useMemo(() => {
    const groups: GroupedKits = {}
    categories.forEach((cat) => {
      groups[cat.category] = []
    })

    kits.forEach((kit) => {
      if (kit.category && groups[kit.category]) {
        groups[kit.category]?.push(kit)
      }
    })

    return groups
  }, [kits])

  /** Get all category keys for the accordion. */
  const allCategoryKeys = useMemo(() => Object.keys(groupedKits), [groupedKits])

  return (
    <div className='w-full max-w-7xl mx-auto rounded-xl bg-white drop-shadow-product-gallery overflow-hidden'>
      <Accordion.Root type='multiple' defaultValue={allCategoryKeys} className='w-full'>
        {categories.map((category) => {
          const categoryKits = groupedKits[category.category] || []

          return (
            <KitAccordionCategoryItem
              key={category.category}
              category={category.category}
              title={category.header}
              description={category.description}
              bgColor={category.headerColour}
              kits={categoryKits}
            />
          )
        })}
      </Accordion.Root>
    </div>
  )
}

export { KitAccordionOverview }
export type { MtaKitStoryblokWithSlug }
